import { Document, Page, Text, View, StyleSheet, Font } from '@react-pdf/renderer';
import fanSizePlusMinus from 'helpers/fanSizePlusMinus';
import PDFHeader from './PDFHeader';
import { rows as jobSheetRows } from './JobSheetPDF';

Font.register({
    family: "Roboto",
    src:
        "https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-regular-webfont.ttf"
});

Font.register({
    family: "Roboto Bold",
    src:
        "https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-bold-webfont.ttf"
});

// Parts that have a Finish dropdown on the job form
const finishParts = ['impeller', 'inletCone', 'case', 'pedestal', 'base', 'driveBeltGuard', 'shaftGuard', 'couplingGuard', 'coolingFin'];

// A part with its own paint/coating goes under Other Finishes instead of External
const ownFinishFields = {
    base: 'baseCoating',
    driveBeltGuard: 'driveBeltGuardPaint',
    shaftGuard: 'shaftGuardPaint',
    couplingGuard: 'couplingGuardPaint',
};

const inspectionItems = [
    'Surface finish acceptable – no runs, sags, or missed areas',
    'Colour consistency and uniformity across all components',
    'No contamination, blistering, cracking, or peeling observed',
    'Edges, welds, and bolt holes adequately coated',
    'Galvanisation Inspection',
];

// Same description line as the job sheet
const describe = ({ name, desc, notes }, attributes) => {
    const details = desc
        .map((f) => {
            switch (f.length) {
                case 1:
                    return attributes[f[0]] ? `${attributes[f[0]]}` : '';
                case 2:
                    return attributes[f[1]] ? `${f[0]} ${attributes[f[1]]}` : '';
                case 3:
                    return f[2](f[0], attributes[f[1]]);
            }
        })
        .filter((i) => i != '')
        .join(', ');

    return `${name} - ${details}${attributes[notes] || ''}`;
};

const isInternal = (finish) => finish === 'Internal' || finish === 'Both';
const isExternal = (finish) => finish === 'External' || finish === 'Both';

export const styles = StyleSheet.create({
    page: {
        position: 'relative',
        fontSize: 10,
        width: '100%',
        marginTop: 15,
        paddingBottom: 30,
    },
    w15: { width: '15%' },
    w20: { width: '20%' },
    w30: { width: '30%' },
    w35: { width: '35%' },
    w65: { width: '65%' },
    w100: { width: '100%' },
    mR: {
        marginRight: 2
    },
    mL: {
        marginLeft: 2
    },
    mT: {
        marginTop: 8
    },
    flex: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'nowrap',
    },
    table: {
        marginLeft: '4%',
        marginRight: '4%',
        width: '92%',
        borderTop: '1px solid grey',
        borderLeft: '1px solid grey',
        borderRight: '1px solid grey',
        borderBottom: '1px solid grey',
    },
    big: {
        fontSize: 16,
        fontFamily: 'Roboto Bold',
    },
    boldText: {
        fontSize: 12,
        fontFamily: 'Roboto Bold',
    },
    boldTextSmaller: {
        fontSize: 9,
        fontFamily: 'Roboto Bold',
    },
    text: {
        fontSize: 12,
        fontFamily: 'Roboto',
    },
    smallText: {
        fontSize: 9,
        marginTop: 2,
        fontFamily: 'Roboto',
    },
    tC: {
        textAlign: 'center'
    },
    bR: {
        borderRight: '1px solid grey',
    },
    cellSmall: {
        width: 25,
        borderRight: '1px solid grey'
    },
    cellDesc: {
        width: 270,
        borderRight: '1px solid grey',
        margin: '0 4 0 2'
    },
    cellSup: {
        width: 100,
        borderRight: '1px solid grey'
    },
    cellItp: {
        width: 100,
        borderRight: '1px solid grey'
    },
    cellReport: {
        width: 50,
        alignItems: 'center',
        justifyContent: 'center',
    },
    checkbox: {
        width: 12,
        height: 12,
        border: '1px solid black',
        marginRight: 3,
    },
    option: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        marginRight: 12,
    },
    line: {
        height: 16,
        borderBottom: '1px solid grey',
    },
});

const Checkbox = () => <View style={styles.checkbox} />;

const Options = ({ labels }) => (
    <View style={[styles.flex, styles.w35]}>
        {labels.map((label) => (
            <View key={label} style={styles.option}>
                <Checkbox />
                <Text style={styles.smallText}>{label}</Text>
            </View>
        ))}
    </View>
);

const FinishHeader = ({ qty, title, lines, supplier, drawing, report, first }) => (
    <View style={[styles.flex, styles.table, first ? {} : styles.mT]} wrap={false}>
        <View style={[styles.cellSmall]}>
            <Text style={[styles.text, styles.tC]}>{qty}</Text>
        </View>
        <View style={[styles.cellDesc]}>
            <Text style={[styles.boldText]}>{title}</Text>
            {lines.filter(Boolean).map((line) => (
                <Text key={line} style={[styles.text]}>{line}</Text>
            ))}
        </View>
        <View style={[styles.cellSup]}>
            <Text style={[styles.text, styles.tC]}>{supplier}</Text>
        </View>
        <View style={[styles.cellItp]}>
            <Text style={[styles.text]}>{drawing}</Text>
        </View>
        <View style={[styles.cellReport]}>
            <Text style={[styles.text]}>{report}</Text>
        </View>
    </View>
);

const FinishItem = ({ qty, description, supplier, drawing }) => (
    <View style={[styles.flex, styles.table]} wrap={false}>
        <View style={[styles.cellSmall]}>
            <Text style={[styles.smallText, styles.tC]}>{qty}</Text>
        </View>
        <View style={[styles.cellDesc]}>
            <Text style={[styles.smallText]}>{description}</Text>
        </View>
        <View style={[styles.cellSup]}>
            <Text style={[styles.smallText, styles.tC]}>{supplier}</Text>
        </View>
        <View style={[styles.cellItp]}>
            <Text style={[styles.smallText]}>{drawing}</Text>
        </View>
        <View style={[styles.cellReport]}>
            <Checkbox />
        </View>
    </View>
);

const PaintPDF = ({ quoteData, jobData }) => {
    const { attributes } = jobData;

    const Aerotech = quoteData.productAreaID === 7 || quoteData.productAreaID === 8;

    const internalItems = [];
    const externalItems = [];
    const otherItems = {};

    jobSheetRows
        .filter(({ field }) => finishParts.includes(field))
        .forEach((row) => {
            const { field } = row;
            if (!(attributes[`${field}Qty`] >= '1')) return;

            const finish = attributes[`${field}Finish`];
            const own = attributes[ownFinishFields[field]];
            const sameAsExternal = own && (own === attributes.externalCoatingType || own === attributes.externalCoatingColour);
            const item = {
                qty: attributes[`${field}Qty`],
                description: describe(row, attributes),
                supplier: attributes[`${field}Supplier`],
                drawing: attributes[`${field}Drawing`],
            };

            if (isInternal(finish)) internalItems.push(item);
            if (own && !sameAsExternal) {
                otherItems[own] = [...(otherItems[own] || []), item];
            } else if (isExternal(finish) || sameAsExternal) {
                externalItems.push(item);
            }
        });

    return (
        <Document>
            <Page wrap size="A4" style={styles.page}>
                <PDFHeader Aerotech={Aerotech} id={jobData.id} isPaint={true} />
                <View style={[styles.flex, styles.table]}>
                    <Text style={[styles.big, styles.mL]}>{attributes.fanQty} x {Aerotech ? '' : 'D'}{attributes.fanDesign} {Aerotech ? '' : 'S'}{attributes.fanSize}{fanSizePlusMinus(attributes.fanSizePlusMinus)}/{attributes.fanPercentageWidth} Arr {attributes.fanArrangement} {attributes.fanHanding} {attributes.fanOrt}{attributes.fanPos ? ` POS ${attributes.fanPos}` : ''} {attributes.fanOther}</Text>
                </View>

                <View style={[styles.flex, styles.table]}>
                    <View style={[styles.cellSmall]}>
                        <Text style={[styles.boldText, styles.tC]}>Qty</Text>
                    </View>
                    <View style={[styles.cellDesc]}>
                        <Text style={[styles.boldText, styles.tC]}>Description</Text>
                    </View>
                    <View style={[styles.cellSup]}>
                        <Text style={[styles.boldText, styles.tC]}>Supplier</Text>
                    </View>
                    <View style={[styles.cellItp]}>
                        <Text style={[styles.boldText, styles.tC]}>Drawing</Text>
                    </View>
                    <View style={[styles.cellReport]}>
                        <Text style={[styles.boldTextSmaller, styles.tC]}>Report/ QC Check</Text>
                    </View>
                </View>

                <FinishHeader
                    first={true}
                    qty={attributes.internalCoatingQty}
                    title='Internal Finish'
                    lines={[attributes.internalCoatingType && `Type: ${attributes.internalCoatingType}`]}
                    supplier={attributes.internalCoatingSupplier}
                    drawing={attributes.internalCoatingDrawing}
                    report={attributes.internalCoatingReport}
                />
                {internalItems.map((item, i) => <FinishItem key={`int-${i}`} {...item} />)}

                <FinishHeader
                    qty={attributes.externalCoatingQty}
                    title='External Finish'
                    lines={[
                        attributes.externalCoatingType && `Type: ${attributes.externalCoatingType}`,
                        attributes.externalCoatingColour && `Colour: ${attributes.externalCoatingColour}`,
                    ]}
                    supplier={attributes.externalCoatingSupplier}
                    drawing={attributes.externalCoatingDrawing}
                    report={attributes.externalCoatingReport}
                />
                {externalItems.map((item, i) => <FinishItem key={`ext-${i}`} {...item} />)}

                {Object.entries(otherItems).map(([type, items]) => (
                    <View key={type}>
                        <FinishHeader title='Other Finishes' lines={[`Type: ${type}`]} />
                        {items.map((item, i) => <FinishItem key={`oth-${i}`} {...item} />)}
                    </View>
                ))}

                <View style={[styles.flex, styles.table, styles.mT]}>
                    <View style={[styles.w100]}>
                        <Text style={[styles.text, styles.mL]}>Collection: dispatch@aireng.com.au or production@aireng.com.au</Text>
                    </View>
                </View>

                <View wrap={false} style={styles.mT}>
                    <View style={[styles.table]}>
                        <Text style={[styles.boldText, styles.mL]}>VISUAL INSPECTION</Text>
                        <Text style={[styles.smallText, styles.mL]}>Note: Visual inspection of all products galvanised, painted, passivated and glass blasted.</Text>
                    </View>
                    <View style={[styles.flex, styles.table]}>
                        <View style={[styles.w65, styles.bR]}>
                            <Text style={[styles.boldTextSmaller, styles.tC]}>Inspection Item</Text>
                        </View>
                        <View style={[styles.w35]}>
                            <Text style={[styles.boldTextSmaller, styles.tC]}>Conforms</Text>
                        </View>
                    </View>
                    {inspectionItems.map((item) => (
                        <View key={item} style={[styles.flex, styles.table]}>
                            <View style={[styles.w65, styles.bR]}>
                                <Text style={[styles.smallText, styles.mL]}>{item}</Text>
                            </View>
                            <Options labels={['Yes', 'No', 'Rework']} />
                        </View>
                    ))}
                    <View style={[styles.flex, styles.table]}>
                        <View style={[styles.w65, styles.bR]}>
                            <Text style={[styles.boldTextSmaller, styles.mL]}>Overall Average DFT – Conforms to Specification:</Text>
                        </View>
                        <Options labels={['Yes', 'No', 'N/A']} />
                    </View>

                    <View style={[styles.table, styles.mT]}>
                        <Text style={[styles.boldTextSmaller, styles.mL]}>COMMENTS / NON-CONFORMANCES:</Text>
                        <View style={styles.line} />
                        <View style={styles.line} />
                        <View style={[styles.line, { borderBottom: 0 }]} />
                    </View>

                    <View style={[styles.table, styles.mT]}>
                        <Text style={[styles.boldTextSmaller, styles.mL]}>QC INSPECTOR SIGN-OFF</Text>
                    </View>
                    <View style={[styles.flex, styles.table, { height: 22 }]}>
                        <View style={[styles.w35, styles.bR]}>
                            <Text style={[styles.boldTextSmaller, styles.mL]}>QC Inspector Signed:</Text>
                        </View>
                        <View style={[styles.w35, styles.bR]}>
                            <Text style={[styles.boldTextSmaller, styles.mL]}>Name:</Text>
                        </View>
                        <View style={[styles.w30]}>
                            <Text style={[styles.boldTextSmaller, styles.mL]}>Date:</Text>
                        </View>
                    </View>
                </View>
            </Page>
        </Document>
    );
};

export default PaintPDF;
